import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import './FeaturedProgramsAdmin.css';
import './ServicesAdmin.css';

const ServicesAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleServiceUpdate = (index, field, value) => {
    updateContent(`${contentPath}.services.${index}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleSectionFieldUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleActionUpdate = (serviceIndex, buttonKey, field, value) => {
    updateContent(`${contentPath}.services.${serviceIndex}.actions.${buttonKey}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleFeatureUpdate = (serviceIndex, featureIndex, value) => {
    updateContent(`${contentPath}.services.${serviceIndex}.features.${featureIndex}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddFeature = (serviceIndex) => {
    const service = sectionData.services[serviceIndex];
    const features = service.features || [];
    updateContent(`${contentPath}.services.${serviceIndex}.features`, [...features, 'Nouvelle caractéristique']);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteFeature = (serviceIndex, featureIndex) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette caractéristique ?')) {
      const service = sectionData.services[serviceIndex];
      const updatedFeatures = service.features.filter((_, i) => i !== featureIndex);
      updateContent(`${contentPath}.services.${serviceIndex}.features`, updatedFeatures);
      setSectionData(getContent(contentPath));
    }
  };

  if (!sectionData) {
    return (
      <div className="services-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="services-admin">
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-briefcase"></i>
            Services Principaux
          </h2>
          <p>Gérez les 4 services : Séminaires, Formation, Coaching, Consulting</p>
        </div>
      </div>

      <div className="section-settings" style={{ marginBottom: '1.5rem' }}>
        <div className="setting-group">
          <label>Titre de la liste des caractéristiques</label>
          <input
            type="text"
            value={sectionData.featuresTitle || ''}
            onChange={(e) => handleSectionFieldUpdate('featuresTitle', e.target.value)}
            placeholder="Ce que vous obtenez :"
          />
        </div>
      </div>

      <div className="services-list-admin">
        {(sectionData.services || []).map((service, index) => (
          <div key={service.id} className={`service-item-admin service-${service.color}`}>
            <div className="service-header">
              <div className="service-title-area">
                <i className={service.icon}></i>
                <h3>{service.title}</h3>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Titre du Service</label>
                <input
                  type="text"
                  value={service.title || ''}
                  onChange={(e) => handleServiceUpdate(index, 'title', e.target.value)}
                  placeholder="Séminaires"
                />
              </div>

              <div className="form-group">
                <label>Icône FontAwesome (fallback si pas d'image)</label>
                <input
                  type="text"
                  value={service.icon || ''}
                  onChange={(e) => handleServiceUpdate(index, 'icon', e.target.value)}
                  placeholder="fas fa-chalkboard-teacher"
                />
              </div>

              <div className="form-group full-width">
                <label>Image du Service</label>
                <ImageUpload
                  value={service.image}
                  onChange={(url) => handleServiceUpdate(index, 'image', url)}
                  label="Télécharger une image"
                />
                <small style={{color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '8px'}}>
                  Recommandé: 800x600px. L'image sera utilisée à la place de l'icône.
                </small>
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={service.description || ''}
                  onChange={(e) => handleServiceUpdate(index, 'description', e.target.value)}
                  placeholder="Description du service..."
                  rows={3}
                />
              </div>
            </div>

            <div className="form-group full-width service-features-group">
              <div className="array-editor">
                <div className="features-section">
                  <div className="features-header">
                    <label>
                      <i className="fas fa-check-circle"></i>
                      Caractéristiques &quot;Ce que vous obtenez&quot;
                    </label>
                    <button
                      type="button"
                      className="btn-add-feature"
                      onClick={() => handleAddFeature(index)}
                    >
                      <i className="fas fa-plus"></i>
                      Ajouter
                    </button>
                  </div>

                  {(service.features || []).length > 0 ? (
                    <div className="features-list">
                      {(service.features || []).map((feature, featureIndex) => (
                        <div key={featureIndex} className="feature-item">
                          <span className="feature-item-index">{featureIndex + 1}</span>
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleFeatureUpdate(index, featureIndex, e.target.value)}
                            placeholder="Ex. Audit de votre pratique"
                          />
                          <button
                            type="button"
                            className="btn-delete-feature"
                            onClick={() => handleDeleteFeature(index, featureIndex)}
                            title="Supprimer cette caractéristique"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="service-features-empty">
                      Aucune caractéristique — cliquez sur Ajouter pour commencer.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group full-width service-actions-group">
              <label><i className="fas fa-mouse-pointer"></i> Bouton principal</label>
              <div className="form-grid">
                <div className="form-group">
                  <label>Texte</label>
                  <input
                    type="text"
                    value={service.actions?.primaryButton?.text || ''}
                    onChange={(e) => handleActionUpdate(index, 'primaryButton', 'text', e.target.value)}
                    placeholder="Découvrir..."
                  />
                </div>
                <div className="form-group">
                  <label>Icône</label>
                  <input
                    type="text"
                    value={service.actions?.primaryButton?.icon || ''}
                    onChange={(e) => handleActionUpdate(index, 'primaryButton', 'icon', e.target.value)}
                    placeholder="fab fa-whatsapp"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Message WhatsApp (prioritaire si rempli)</label>
                  <input
                    type="text"
                    value={service.actions?.primaryButton?.whatsappMessage || ''}
                    onChange={(e) => handleActionUpdate(index, 'primaryButton', 'whatsappMessage', e.target.value)}
                    placeholder="Bonjour, je suis intéressé(e) par..."
                  />
                </div>
                <div className="form-group full-width">
                  <label>Lien interne ou externe (si pas WhatsApp)</label>
                  <input
                    type="text"
                    value={service.actions?.primaryButton?.link || ''}
                    onChange={(e) => handleActionUpdate(index, 'primaryButton', 'link', e.target.value)}
                    placeholder="/formations ou https://..."
                  />
                </div>
              </div>
            </div>

            <div className="form-group full-width service-actions-group">
              <label><i className="fas fa-link"></i> Bouton secondaire (optionnel)</label>
              <div className="form-grid">
                <div className="form-group">
                  <label>Texte</label>
                  <input
                    type="text"
                    value={service.actions?.secondaryButton?.text || ''}
                    onChange={(e) => handleActionUpdate(index, 'secondaryButton', 'text', e.target.value)}
                    placeholder="Plus d'informations"
                  />
                </div>
                <div className="form-group">
                  <label>Icône</label>
                  <input
                    type="text"
                    value={service.actions?.secondaryButton?.icon || ''}
                    onChange={(e) => handleActionUpdate(index, 'secondaryButton', 'icon', e.target.value)}
                    placeholder="fas fa-info-circle"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Lien</label>
                  <input
                    type="text"
                    value={service.actions?.secondaryButton?.link || ''}
                    onChange={(e) => handleActionUpdate(index, 'secondaryButton', 'link', e.target.value)}
                    placeholder="/contact"
                  />
                </div>
              </div>
            </div>

            <div className="service-info-box">
              <i className="fas fa-info-circle"></i>
              <div>
                <strong>Affichage des boutons :</strong>
                <ul>
                  <li>WhatsApp si un message est renseigné</li>
                  <li>Sinon lien interne/externe si renseigné</li>
                  <li>Formation → /formations ; Coaching → /experts (repli automatique)</li>
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesAdmin;
